<script lang="ts">
  import Accordion from '$lib/components/accordion/Accordion.svelte';
  import H from '$lib/components/H.svelte';
  import LoadingSpinner from '$lib/components/LoadingSpinner.svelte';
  import { errorProvider } from '$lib/error-handling/errorProvider';
  import MaterialIcon from '$lib/MaterialIcon.svelte';
  import { mdiCheck, mdiClose } from '@mdi/js';
  import type { WeightedAnswerAndData } from 'shared/definitions/checkpoint-definitions';
  import { onMount } from 'svelte';

  export let question: WeightedAnswerAndData;

  let studentAnswerSVG: string | undefined;
  let questionSVG: string | undefined;
  let state: 'loading' | 'loaded' | 'errored' = 'loading';
  export let open = false;

  // On mount, load the images:
  onMount(async () => {
    try {
      const { studentAnswerSVGURL, questionSVGURL } = question;
      const [loadedStudentAnswerSVG, loadedQuestionSVG] = await Promise.all([
        studentAnswerSVGURL
          ? ((await fetch(new Request(studentAnswerSVGURL)).then((res) => res.text())) as string)
          : undefined,
        questionSVGURL
          ? ((await fetch(new Request(questionSVGURL)).then((res) => res.text())) as string)
          : undefined,
      ]);
      studentAnswerSVG = loadedStudentAnswerSVG;
      questionSVG = loadedQuestionSVG;
      state = 'loaded';
    } catch (err) {
      state = 'errored';
      errorProvider.error(err);
    }

    // Unwanted BR's (those that were useful when displaying the question to the student
    // but that are not for showing it in the title bar) are hidden by CSS below --
    // but that removes any between the prior and later content. So, we add a space:
    Array.from(questionTitleSpan?.querySelectorAll<HTMLBRElement>('br') || []).forEach(
      (node: HTMLBRElement) => {
        node.insertAdjacentText('afterend', ' ');
      }
    );
  });

  function sizeSvg(svgString: string) {
    // Remove width:
    svgString = svgString.replace(/<svg(?:[^>]+?)(width=".+?")(?:.+?)>/, (match, p1) =>
      match.replace(p1, '')
    );
    svgString = svgString.replace(/<svg(?:[^>]+?)(height=".+?")(?:.+?)>/, (match, p1) =>
      match.replace(p1, '')
    );

    svgString = svgString.replace(/<svg(?:[^>]+?)(style=".+?")(?:.+?)>/, (match, p1) =>
      match.replace(p1, '')
    );

    const div = document.createElement('div');
    div.innerHTML = svgString;
    const svg = div.firstElementChild as SVGSVGElement;
    svg.style.maxHeight = '250px';
    const s = new XMLSerializer();
    return s.serializeToString(svg);
  }

  let questionTitleSpan: HTMLSpanElement;
</script>

<div class="overflow-hidden">
  <Accordion
    class="mb-1 border"
    on:open={(evt) => {
      evt.preventDefault();
    }}
    titleClass="w-full"
    contentClass="text-sm px-4 py-2"
    bind:open
  >
    <span slot="title">
      <H level={4} class="text-sm font-normal text-left text-gray bg-gray-50">
        <MaterialIcon
          path={question.weightedWrongAnswers ? mdiClose : mdiCheck}
          label="Wrong answer"
          size="1em"
          class="px-2 {question.weightedWrongAnswers ? 'text-red' : 'text-green'}"
        />
        <span class="question-title" bind:this={questionTitleSpan}>
          {@html question.questionText}
        </span>
      </H>
    </span>
    <span slot="content">
      {#if question.timeRanOut}
        <p class="text-sm my-2">Time expired before this answer was submitted.</p>
      {:else if question.answerSpeed && question.timeRanOut !== null}
        <p class="text-sm my-2">
          Time on question: {(question.answerSpeed / 1000).toFixed(1)} seconds
        </p>
      {/if}
      {#if questionSVG}
        <div>
          {@html sizeSvg(questionSVG)}
        </div>
      {/if}
      <div>
        {#if state === 'loading'}
          <LoadingSpinner /> - Loading question images
        {:else if state === 'errored'}
          <span class="text-red">Unable to load question images.</span>
        {/if}
        <div>
          <H level={5} class="text-sm font-normal inline">Student Answer:</H>
          {#if question.studentAnswerText}
            {@html question.studentAnswerText}
          {/if}
          {#if studentAnswerSVG}
            <div class="">
              {@html sizeSvg(studentAnswerSVG)}
            </div>
          {/if}
        </div>
        {#if question.correctAnswerText}
          <div>
            <H level={5} class="text-sm font-normal inline">Correct Answer:</H>
            {@html question.correctAnswerText}
          </div>
        {/if}
      </div></span
    >
  </Accordion>
</div>

<style lang="postcss">
  .question-title {
    :global(br) {
      display: none;
    }

    :global(.utheory-time-signature) {
      :global(br) {
        display: block;
      }
    }
  }
</style>
